'use client';

/**
 * NumberCell Component
 *
 * Renders numbers with localization and formatting support.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { defaultTheme, getTextStyles } from '../theme/tokens';

interface NumberCellProps {
  value: number | null | undefined;
  format?: 'integer' | 'decimal' | 'currency' | 'percentage';
  locale?: string;
  currency?: string;
  decimals?: number;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const NumberCell: React.FC<NumberCellProps> = ({
  value,
  format = 'decimal',
  locale = 'en-US',
  currency = 'USD',
  decimals,
  align = 'right',
  className,
}) => {
  // Get typography styles from theme (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  if (value == null) {
    return (
      <div
        className={cn(
          alignClasses[align],
          className
        )}
        style={textStyles.secondary()}
      >
        -
      </div>
    );
  }

  let displayValue: string;

  try {
    switch (format) {
      case 'integer':
        displayValue = new Intl.NumberFormat(locale, {
          maximumFractionDigits: 0,
        }).format(value);
        break;

      case 'decimal':
        displayValue = new Intl.NumberFormat(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
        break;

      case 'currency':
        displayValue = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: decimals ?? 2,
          maximumFractionDigits: decimals ?? 2,
        }).format(value);
        break;

      case 'percentage':
        displayValue = new Intl.NumberFormat(locale, {
          style: 'percent',
          minimumFractionDigits: decimals ?? 0,
          maximumFractionDigits: decimals ?? 2,
        }).format(value / 100);
        break;

      default:
        displayValue = String(value);
    }
  } catch (error) {
    console.warn('NumberCell formatting error:', error);
    displayValue = String(value);
  }

  return (
    <div
      className={cn(
        alignClasses[align],
        'tabular-nums', // Use tabular numbers for better alignment
        className
      )}
      style={textStyles.primary()}
    >
      {displayValue}
    </div>
  );
};
